import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { 
  ArrowRight, Zap, GitBranch, Bell, Download, Mail, Brain,
  Sparkles, Palette, FileSignature, Shield
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter" }}>
      <MarketingNav />

      <section className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-600 mb-4">ABOUT</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zinc-950 tracking-tight leading-tight mb-6" style={{ fontFamily: "Inter" }}>Why HeyIntake Exists</h1>
            <p className="text-lg text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              Service businesses rely on intake forms to collect information — but collecting data isn't the same as understanding it. We built HeyIntake to transform static forms into intelligent workflows.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6 bg-zinc-50">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-zinc-950 tracking-tight mb-6" style={{ fontFamily: "Inter" }}>More Than Just a Form Builder</h2>
            <p className="text-lg text-zinc-500 mb-4">Most tools stop at collecting responses.</p>
            <p className="text-lg text-zinc-500 mb-4">HeyIntake analyzes each submission, assigns an AI score, generates a summary, and places it directly into your pipeline.</p>
            <p className="text-lg text-indigo-600 font-medium">From first inquiry to closed deal, you can manage everything in one place.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-zinc-950 tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Clarity Over Complexity</h2>
            <p className="text-lg text-zinc-500 mb-8">We believe intake should reduce friction — not create more tools to manage.</p>
            <p className="text-lg text-zinc-950 font-semibold mb-6">HeyIntake combines:</p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { icon: <Brain className="w-5 h-5" />, text: "AI lead scoring & summaries" },
                { icon: <Sparkles className="w-5 h-5" />, text: "AI Form Builder" },
                { icon: <Zap className="w-5 h-5" />, text: "Smart automations (Auto Flows)" },
                { icon: <GitBranch className="w-5 h-5" />, text: "Unified lead → intake pipeline" },
                { icon: <FileSignature className="w-5 h-5" />, text: "E-signature & agreements" },
                { icon: <Bell className="w-5 h-5" />, text: "Email templates & notifications" },
                { icon: <Palette className="w-5 h-5" />, text: "Custom branding (logo + colors)" },
                { icon: <Shield className="w-5 h-5" />, text: "Consent checkbox & secure links" },
                { icon: <Download className="w-5 h-5" />, text: "Document collection & reminders" },
                { icon: <Mail className="w-5 h-5" />, text: "Guided setup & onboarding" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">{item.icon}</div>
                  <span className="text-zinc-800 font-medium text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-zinc-500 mt-8">All without requiring a full enterprise CRM.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6 bg-zinc-50">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-zinc-950 tracking-tight mb-6" style={{ fontFamily: "Inter" }}>Company Background</h2>
            <p className="text-lg text-zinc-500 mb-4">HeyIntake is developed and operated by <strong className="text-zinc-950">Okliko Inc.</strong>, a Delaware-incorporated technology company focused on building AI-powered growth and workflow tools for service businesses.</p>
            <p className="text-lg text-zinc-500">Our mission is simple: reduce friction, increase clarity, and help businesses make better decisions from their inbound inquiries.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Ready to get started?</h2>
            <p className="text-lg text-zinc-400 mb-8">Start your free trial today. No credit card required.</p>
            <Link to="/register"><Button className="h-14 px-10 text-base rounded-full bg-indigo-600 text-white hover:bg-indigo-700">Start Trial <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
